import {
  RefObject,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { CopilotActionId } from 'components/copilotActions';

type UsePinnedActionsCollapseResult = {
  containerRef: RefObject<HTMLDivElement>;
  collapsedIds: Set<CopilotActionId>;
};

/**
 * Drops pinned action labels one at a time, left to right, until the row fits
 * the toolbar — so the rightmost pinned button keeps its label longest — and
 * restores them when the toolbar grows again.
 */
export const usePinnedActionsCollapse = (
  ids: CopilotActionId[],
): UsePinnedActionsCollapseResult => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [collapsedCount, setCollapsedCount] = useState(0);
  // Bumped to re-run the measurement when nothing else changed.
  const [measureToken, setMeasureToken] = useState(0);
  const idsKey = ids.join(',');

  // The pinned set changed, so start from all labels shown.
  useLayoutEffect(() => {
    setCollapsedCount(0);
  }, [idsKey]);

  // The toolbar is what actually decides how much room there is. Depends on
  // idsKey because the container only exists once something is pinned.
  useEffect(() => {
    const toolbar = containerRef.current?.parentElement;

    if (!toolbar) {
      return;
    }

    let previousWidth = toolbar.clientWidth;

    const observer = new ResizeObserver(([entry]) => {
      const width = entry.contentRect.width;
      const grew = width > previousWidth;
      previousWidth = width;

      // Only a wider toolbar can fit labels that were dropped. Narrowing just
      // needs a re-measure, so resetting there would collapse from scratch on
      // every callback while a window is dragged.
      if (grew) {
        setCollapsedCount(0);
      }

      setMeasureToken((token) => token + 1);
    });

    observer.observe(toolbar);

    return () => {
      observer.disconnect();
    };
  }, [idsKey]);

  // Reading scrollWidth forces layout, so this runs only when a measurement is
  // actually due — never on the editor's per-keystroke re-renders.
  useLayoutEffect(() => {
    const container = containerRef.current;

    if (!container || collapsedCount >= ids.length) {
      return;
    }

    if (container.scrollWidth > container.clientWidth) {
      setCollapsedCount((count) => count + 1);
    }
  }, [collapsedCount, measureToken, idsKey, ids.length]);

  const collapsedIds = useMemo(
    () => new Set(ids.slice(0, collapsedCount)),
    [ids, collapsedCount],
  );

  return { containerRef, collapsedIds };
};
